import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { Close, FolderOpen } from "@mui/icons-material";
import { hostname, parseError } from "../../../utils";
import axios from "axios";
import { Document, FolderShare } from "../../../types";
import {
  FOLDER_SHARE_TYPE_COMPANY,
  FOLDER_SHARE_TYPE_DIRECT,
  FOLDER_SHARE_TYPE_PUBLIC,
} from "../../../constants";
import LoadingButton from "../../../components/LoadingButton";
import { useWhitelabel } from "../../../context/WhitelabelContext";
import { hasAdminAccessByRole } from "../../../utils/roleUtils";

interface ShareFolderDialogProps {
  open: boolean;
  onClose: () => void;
  folder: Document | null;
  companyId: string;
  getToken: () => Promise<string>;
  onShareCreated?: () => void;
  userRole?: string | null;
}

const ShareFolderDialog: React.FC<ShareFolderDialogProps> = ({
  open,
  onClose,
  folder,
  companyId,
  getToken,
  onShareCreated,
  userRole,
}) => {
  const canShareExternally = hasAdminAccessByRole(userRole ?? null);
  const { whitelabelSettings } = useWhitelabel();
  const publicFoldersEnabled = whitelabelSettings?.publicFolderFilter !== "none";
  const [shareType, setShareType] = useState<string>(FOLDER_SHARE_TYPE_COMPANY);
  const [targetCompanyId, setTargetCompanyId] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [description, setDescription] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Existing shares for this folder (needed to delete when making private)
  const [existingShares, setExistingShares] = useState<FolderShare[]>([]);


  // Fetch existing shares for this folder (needed to delete when making private)
  const fetchExistingShares = async () => {
    if (!folder) return;

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/folders/${folder.id}/shares`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const shares = response.data.shares || [];
      setExistingShares(shares);

      // Set the initial share type based on existing shares
      // Priority: public > direct > company > none
      if (shares.some((s: FolderShare) => s.shareType === FOLDER_SHARE_TYPE_PUBLIC)) {
        setShareType(FOLDER_SHARE_TYPE_PUBLIC);
      } else if (shares.some((s: FolderShare) => s.shareType === FOLDER_SHARE_TYPE_DIRECT)) {
        setShareType(FOLDER_SHARE_TYPE_DIRECT);
      } else if (shares.some((s: FolderShare) => s.shareType === FOLDER_SHARE_TYPE_COMPANY)) {
        setShareType(FOLDER_SHARE_TYPE_COMPANY);
      } else {
        setShareType("none");
      }
    } catch (err: any) {
      console.error("Error fetching existing shares:", err);
      // Fall back to folder's shareVisibility if fetch fails
      if (folder.shareVisibility) {
        setShareType(folder.shareVisibility);
      } else {
        setShareType("none");
      }
    }
  };

  useEffect(() => {
    if (open && folder) {
      // Reset form first
      setTargetCompanyId("");
      setContactEmail("");
      setDescription("");
      setMessage("");
      setError("");
      // Then fetch shares which will set the correct shareType
      fetchExistingShares();
    }
  }, [open, folder]);

  const handleCreateShare = async () => {
    if (!folder) return;

    // Handle "none" - delete all existing shares
    if (shareType === "none") {
      setLoading(true);
      setError("");
      try {
        const token = await getToken();
        // Delete all existing shares for this folder
        for (const share of existingShares) {
          await axios.delete(`${hostname}/folder-shares/${share.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        if (onShareCreated) {
          onShareCreated();
        }
        onClose();
      } catch (err: any) {
        setError(parseError(err));
      } finally {
        setLoading(false);
      }
      return;
    }

    // Validate based on share type
    if (shareType === FOLDER_SHARE_TYPE_PUBLIC && !contactEmail) {
      setError("Contact email is required for public shares");
      return;
    }

    if (shareType === FOLDER_SHARE_TYPE_DIRECT && !targetCompanyId.trim()) {
      setError("Please enter a company ID to share with");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/folder-shares`,
        {
          folderId: folder.id,
          shareType,
          targetCompanyId:
            shareType === FOLDER_SHARE_TYPE_DIRECT ? targetCompanyId.trim() : null,
          contactEmail:
            shareType === FOLDER_SHARE_TYPE_PUBLIC ? contactEmail : null,
          description: description || null,
          message: shareType === FOLDER_SHARE_TYPE_DIRECT ? message : null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (onShareCreated) {
        onShareCreated();
      }

      // Close dialog after successful share
      onClose();
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "60vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <FolderOpen color="primary" />
            <Box>
              <Typography variant="h6">Share Folder</Typography>
              <Typography variant="body2" color="text.secondary">
                {folder?.name}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Share Settings
        </Typography>

        <FormControl component="fieldset" sx={{ width: "100%", mb: 2 }}>
          <RadioGroup
            value={shareType}
            onChange={(e) => setShareType(e.target.value)}
          >
            <FormControlLabel
              value="none"
              control={<Radio />}
              label={
                <Box>
                  <Typography>None (Private)</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only you can access this folder
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value={FOLDER_SHARE_TYPE_COMPANY}
              control={<Radio />}
              label={
                <Box>
                  <Typography>Company-wide</Typography>
                  <Typography variant="caption" color="text.secondary">
                    All users in your company can access this folder
                  </Typography>
                </Box>
              }
            />
            {canShareExternally && (
              <FormControlLabel
                value={FOLDER_SHARE_TYPE_DIRECT}
                control={<Radio />}
                label={
                  <Box>
                    <Typography>Share with specific company</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Send an invitation that requires approval
                    </Typography>
                  </Box>
                }
              />
            )}
            {canShareExternally && publicFoldersEnabled && (
              <FormControlLabel
                value={FOLDER_SHARE_TYPE_PUBLIC}
                control={<Radio />}
                label={
                  <Box>
                    <Typography>Public</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Anyone on the platform can discover and view this folder
                    </Typography>
                  </Box>
                }
              />
            )}
          </RadioGroup>
        </FormControl>

        {/* Direct share company selection */}
        {shareType === FOLDER_SHARE_TYPE_DIRECT && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Company ID"
              placeholder="Enter the company ID to share with..."
              value={targetCompanyId}
              onChange={(e) => setTargetCompanyId(e.target.value)}
              helperText="Ask the recipient to copy their Company ID from their Companies list menu"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Message (optional)"
              placeholder="Add a message with your invitation..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        )}

        {/* Public share fields */}
        {shareType === FOLDER_SHARE_TYPE_PUBLIC && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Public folders are visible to all users on the platform. Make sure
              you don't share sensitive or confidential information.
            </Alert>
            <TextField
              fullWidth
              label="Contact Email"
              placeholder="email@company.com"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              required
              helperText="Users can contact this email for questions about the shared folder"
              sx={{ mb: 2 }}
            />
          </Box>
        )}

        {/* Description (for all share types) */}
        <TextField
          fullWidth
          label="Description (optional)"
          placeholder="Describe what this folder contains..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={2}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          onClick={handleCreateShare}
          loading={loading}
          disabled={
            (shareType === FOLDER_SHARE_TYPE_DIRECT && !targetCompanyId.trim()) ||
            (shareType === FOLDER_SHARE_TYPE_PUBLIC && !contactEmail) ||
            (shareType === "none" && existingShares.length === 0)
          }
        >
          {shareType === "none"
            ? "Make Private"
            : shareType === FOLDER_SHARE_TYPE_DIRECT
            ? "Send Invitation"
            : "Share"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ShareFolderDialog;
